import { ArrowLeft, GripVertical, MessageSquare, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import { Button } from "@/components/ui/button";
import { ConfirmDestructiveDialog } from "@/components/ui/confirm-destructive-dialog";
import { ErrorState } from "@/components/feedback/ErrorState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingState } from "@/components/feedback/LoadingState";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useLlmModels } from "@/features/llms/hooks/useLlmModels";
import { useMyInstalledAgents } from "@/features/marketplace/hooks/useMarketplace";
import {
  useCreateTeamSession,
  useDeleteTeamSession,
  useReplaceTeamSessionAgents,
  useTeamSession,
  useUpdateTeamSession,
} from "@/features/team-sessions/hooks/useTeamSessions";

/**
 * Editor minimal de team session. Drag-and-drop fica para polish
 * futuro -- por ora ordering eh via botoes up/down.
 */
export function TeamSessionEditorPage() {
  const { t } = useTranslation("teamSessions");
  const { t: tc } = useTranslation("common");
  const { sessionId } = useParams<{ sessionId: string }>();
  const isNew = !sessionId || sessionId === "new";
  const navigate = useNavigate();
  const detail = useTeamSession(isNew ? "" : sessionId!);
  const installed = useMyInstalledAgents();
  const llms = useLlmModels();
  const createMut = useCreateTeamSession();
  const updateMut = useUpdateTeamSession(sessionId ?? "");
  const replaceMut = useReplaceTeamSessionAgents(sessionId ?? "");
  const deleteMut = useDeleteTeamSession();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [supervisorId, setSupervisorId] = useState<string>("");
  const [supervisorLlmId, setSupervisorLlmId] = useState<string>("");
  const [maxDelegations, setMaxDelegations] = useState(10);
  const [agentIds, setAgentIds] = useState<string[]>([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    if (!isNew && detail.data) {
      setName(detail.data.name);
      setDescription(detail.data.description ?? "");
      setInstructions(detail.data.instructions ?? "");
      setSupervisorId(detail.data.supervisor_agent_id ?? "");
      setSupervisorLlmId(detail.data.supervisor_llm_model_id ?? "");
      setMaxDelegations(detail.data.max_delegations);
      setAgentIds(detail.data.agents.map((a) => a.agent_id));
    }
  }, [isNew, detail.data]);

  if (!isNew && detail.isLoading) return <LoadingState />;
  if (!isNew && detail.isError)
    return <ErrorState error={detail.error} />;

  const installedAgents = installed.data ?? [];
  const installedById = new Map(installedAgents.map((a) => [a.id, a]));

  function move(idx: number, delta: number) {
    const next = [...agentIds];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    const a = next[idx];
    const b = next[target];
    if (a === undefined || b === undefined) return;
    next[idx] = b;
    next[target] = a;
    setAgentIds(next);
  }
  function remove(idx: number) {
    setAgentIds(agentIds.filter((_, i) => i !== idx));
  }
  function add(agentId: string) {
    if (!agentIds.includes(agentId)) {
      setAgentIds([...agentIds, agentId]);
    }
  }

  async function handleSave() {
    if (agentIds.length === 0) {
      toast.error(t("editor.addAtLeastOneAgent"));
      return;
    }
    try {
      // supervisor_llm_model_id so faz sentido quando supervisor_agent_id
      // eh null (modo "neutro"). Em modo "agente especifico" o LLM vem
      // do proprio agente.
      const neutralLlmId = supervisorId ? undefined : supervisorLlmId;
      if (isNew) {
        const created = await createMut.mutateAsync({
          name,
          description: description || undefined,
          instructions: instructions || undefined,
          supervisor_agent_id: supervisorId || undefined,
          supervisor_llm_model_id: neutralLlmId || undefined,
          max_delegations: maxDelegations,
          agent_ids: agentIds,
        });
        toast.success(t("editor.sessionCreated"));
        navigate(`/sessions/${created.id}`);
      } else {
        await updateMut.mutateAsync({
          name,
          description,
          instructions,
          supervisor_agent_id: supervisorId || null,
          supervisor_llm_model_id: supervisorId
            ? null
            : supervisorLlmId || null,
          max_delegations: maxDelegations,
        });
        await replaceMut.mutateAsync({ agent_ids: agentIds });
        toast.success(t("editor.sessionSaved"));
      }
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleDelete() {
    if (!sessionId || isNew) return;
    try {
      await deleteMut.mutateAsync(sessionId);
      toast.success(t("editor.sessionDeleted"));
      setConfirmDeleteOpen(false);
      navigate("/sessions");
    } catch (err) {
      toastApiError(err);
    }
  }

  if (installedAgents.length === 0 && isNew) {
    return (
      <div className="space-y-3 rounded-2xl border border-dashed border-line bg-surface p-6 text-sm text-ink-700">
        <p>{t("editor.agentsEmpty")}</p>
        <Button onClick={() => navigate("/marketplace")}>
          {t("editor.openMarketplace")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="animate-fade-up relative flex flex-wrap items-end justify-between gap-3 pl-5">
        <span className="absolute left-0 top-1 h-[calc(100%-0.5rem)] w-[3px] rounded-full bg-gradient-to-b from-brand-500 to-brand-700" />
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/sessions")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-balance text-[28px] font-semibold leading-[1.05] tracking-[-0.02em] text-ink-950 sm:text-[32px]">
            {isNew ? t("editor.newTitle") : t("editor.title")}
          </h1>
        </div>
        <div className="flex gap-2">
          {!isNew ? (
            <>
              <Button
                variant="outline"
                onClick={() => navigate(`/sessions/${sessionId}/chat`)}
              >
                <MessageSquare className="h-4 w-4" />
                {t("actions.openChat")}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setConfirmDeleteOpen(true)}
              >
                {tc("actions.delete")}
              </Button>
            </>
          ) : null}
          <Button onClick={handleSave}>{tc("actions.save")}</Button>
        </div>
      </header>

      <div className="space-y-2">
        <Label htmlFor="ts-name">{t("editor.name")} *</Label>
        <Input id="ts-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ts-desc">{t("editor.description")}</Label>
        <Textarea
          id="ts-desc"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ts-instructions">{t("editor.instructions")}</Label>
        <Textarea
          id="ts-instructions"
          rows={3}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          {t("editor.instructionsHint")}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>{t("editor.supervisor")}</Label>
          <Select
            value={supervisorId || "neutral"}
            onValueChange={(v) => setSupervisorId(v === "neutral" ? "" : v)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("editor.supervisorPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="neutral">
                {t("editor.supervisorPlaceholder")}
              </SelectItem>
              {agentIds.map((aid) => {
                const a = installedById.get(aid);
                return a ? (
                  <SelectItem key={aid} value={aid}>
                    {a.name}
                  </SelectItem>
                ) : null;
              })}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {t("editor.supervisorHint")}
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ts-max">{t("editor.maxDelegations")}</Label>
          <Input
            id="ts-max"
            type="number"
            min={1}
            max={50}
            value={maxDelegations}
            onChange={(e) => setMaxDelegations(Number(e.target.value) || 10)}
          />
          <p className="text-xs text-muted-foreground">
            {t("editor.maxDelegationsHint")}
          </p>
        </div>
      </div>

      {/* Modelo do supervisor neutro: so faz sentido quando NAO ha
          supervisor_agent_id (no modo "agente especifico" o LLM vem do
          proprio agente). */}
      {!supervisorId ? (
        <div className="space-y-2">
          <Label>{t("editor.supervisorLlm")}</Label>
          <Select
            value={supervisorLlmId || "default"}
            onValueChange={(v) =>
              setSupervisorLlmId(v === "default" ? "" : v)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder={t("editor.supervisorLlmPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">
                {t("editor.supervisorLlmPlaceholder")}
              </SelectItem>
              {(llms.data ?? [])
                .filter((m) => m.is_active)
                .map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.display_name} ({m.provider})
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {t("editor.supervisorLlmHint")}
          </p>
        </div>
      ) : null}

      <div className="space-y-2">
        <Label>{t("editor.agents")}</Label>
        <ul className="space-y-1">
          {agentIds.map((aid, idx) => {
            const a = installedById.get(aid);
            return (
              <li
                key={aid}
                className="flex items-center justify-between rounded border p-2"
              >
                <div className="flex items-center gap-2">
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    {idx + 1}.
                  </span>
                  <span className="text-sm">{a?.name ?? t("editor.removedAgent")}</span>
                </div>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                  >
                    ↑
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => move(idx, 1)}
                    disabled={idx === agentIds.length - 1}
                  >
                    ↓
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(idx)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
        <Select onValueChange={(v) => add(v)}>
          <SelectTrigger>
            <SelectValue placeholder={t("editor.addAgentPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            {installedAgents
              .filter((a) => !agentIds.includes(a.id))
              .map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {!isNew && sessionId ? (
        <ConfirmDestructiveDialog
          open={confirmDeleteOpen}
          onOpenChange={setConfirmDeleteOpen}
          title={t("editor.confirmDeleteTitle")}
          description={t("editor.confirmDeleteDescription")}
          resourceName={name || detail.data?.name || ""}
          resourceKind={t("editor.confirmDeleteResourceKind")}
          confirmLabel={t("editor.confirmDeleteLabel")}
          onConfirm={handleDelete}
          isPending={deleteMut.isPending}
        />
      ) : null}
    </div>
  );
}
